import { FormattedMessage, useIntl } from 'react-intl';
import {
    Button,
    Box,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
} from '@chakra-ui/react';

import { Formik, Form } from 'formik';

import useCommonToast from '../../hooks/useCommonToast';

import InputFormikField from '../forms/InputFormikField';
import ComboboxFormikField from '../forms/ComboboxFormikField';

const AddSubBatteryModal = ({ headerItem, batteryTypes, batteryItemId, updateBatteries, onSubmit, ...rest }) => {
    const intl = useIntl();
    const toast = useCommonToast();

    const formLabels = {
        description: intl.formatMessage({ id: 'batteries_sub_battery_heading' }),
        batteryType: intl.formatMessage({ id: 'batteries_battery_type' }),
        emptyPlaceholder: ' ',
    };

    const initialFormValues = {
        description: '',
        batteryTypeId: '',
        itemId: parseInt(batteryItemId), // the parent battery item id
    };

    const validateDescription = (value) => {
        const trimmed = value.trim();

        if (!trimmed) {
            return intl.formatMessage({ id: 'common_forms_validation_required' }, { label: 'Installation value' });
        }

        if (trimmed.length > 50) {
            return intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label: 'Installation', lengthRule: '50 characters or less' }
            );
        }
    };

    const onFormSubmit = async ({ description, batteryTypeId, itemId }) => {
        const successMessage = intl.formatMessage({ id: 'common_item_creation_success' }, { type: headerItem });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            const { data } = await onSubmit({ description, batteryTypeId: batteryTypeId || null, itemId });
            updateBatteries(data, itemId);
            rest.onClose();
            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    return (
        <Modal {...rest}>
            <ModalOverlay />

            <ModalContent pt={3}>
                <ModalHeader pr={12}>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="common_add_subitem_modal_heading" values={{ item: headerItem }} />
                    </Heading>
                </ModalHeader>

                <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                <Formik enableReinitialize initialValues={initialFormValues} onSubmit={onFormSubmit}>
                    {({ isSubmitting, isValid, touched }) => (
                        <Form>
                            <ModalBody borderBottom="1px" borderColor="gray.200">
                                <InputFormikField
                                    name="description"
                                    type="text"
                                    label={formLabels.description}
                                    isRequired
                                    validate={validateDescription}
                                />

                                <ComboboxFormikField
                                    id="batteryTypeId"
                                    name="batteryTypeId"
                                    label={formLabels.batteryType}
                                    mt={3}
                                    options={batteryTypes}
                                    valueKey="id"
                                    labelKey="description"
                                    showPlaceholder={true}
                                    placeholderValue=""
                                />
                            </ModalBody>

                            <ModalFooter justifyContent="stretch" flexWrap="wrap">
                                <Box ml="auto">
                                    <Button variant="secondary" onClick={rest.onClose}>
                                        <Box as="span" textTransform="capitalize">
                                            <FormattedMessage id="common_cancel" />
                                        </Box>
                                    </Button>

                                    <Button
                                        type="submit"
                                        // the below expression verifies that all required fields are populated before enabling the submit button
                                        isDisabled={!isValid || Object.keys(touched).length === 0}
                                        isLoading={isSubmitting}
                                        ml={3}
                                    >
                                        <FormattedMessage id="common_item_create_btn" />
                                    </Button>
                                </Box>
                            </ModalFooter>
                        </Form>
                    )}
                </Formik>
            </ModalContent>
        </Modal>
    );
};

export default AddSubBatteryModal;
